import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import StageLoader from "./StageLoader";
import { getApiErrorMessage } from "../lib/errorMessages";
import { RESUME_ACCEPT as ACCEPT, RESUME_MAX_BYTES as MAX_BYTES } from "../lib/resumeUpload";
import ResumeUploadHint from "./ResumeUploadHint";
import { parseResumeWithProgress, saveCareerProfile, uploadResume } from "../lib/api";
import { useAuth } from "../lib/AuthContext";


const emptyExtracted = {
  headline: "",
  current_role: "",
  current_company: "",
  total_years: null,
  seniority_estimate: "",
  skills: [],
  technologies: [],
  domains: [],
  experience: [],
  projects: [],
  leadership_evidence: [],
  scale_depth_evidence: [],
  education: [],
  certifications: [],
  achievements: [],
};

// Mirrors ResumeProfile.jsx's buildProfilePayload — kept local rather than
// shared since that file's source is exact-string tested (ResumeProfile.test.js).
const buildProfilePayload = (resumeId, parseRunId, draftData) => ({
  source_resume_id: resumeId,
  source_parse_run_id: parseRunId,
  profile: {
    headline: draftData.headline,
    current_role: draftData.current_role,
    current_title: draftData.current_role,
    current_company: draftData.current_company,
    total_years: draftData.total_years,
    seniority_estimate: draftData.seniority_estimate,
    seniority_level: draftData.seniority_estimate,
    skills: draftData.skills || [],
    technologies: draftData.technologies || [],
    domains: draftData.domains || [],
    experience: draftData.experience || [],
    projects: draftData.projects || [],
    leadership_evidence: draftData.leadership_evidence || [],
    scale_depth_evidence: draftData.scale_depth_evidence || [],
    education: draftData.education || [],
    certifications: draftData.certifications || [],
    achievements: draftData.achievements || [],
  },
});

const PROGRESS_STAGE_PERCENT = {
  reading: 15,
  skills: 30,
  experience: 50,
  leadership: 70,
  scale: 85,
  complete: 100,
};

// Replaces the /onboarding wizard (already removed — the resume upload page
// itself became onboarding) as the very first thing a brand-new user sees
// right after signup. No review step: extraction auto-saves, matching the
// locked product decision already in place on /profile/resume.
export default function ResumeUploadModal({ open, onSkip, onDone }) {
  const { setUser } = useAuth();
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null); // { stage, message, percent } | null
  const [error, setError] = useState("");

  // Closing the modal (or leaving the page) mid-upload drops the request.
  useEffect(() => () => abortRef.current?.abort(), []);

  if (!open) return null;

  const busy = !!progress;

  const runParse = (resumeId, signal) => {
    setProgress({ stage: "reading", message: "Starting extraction...", percent: 5 });
    parseResumeWithProgress(resumeId, {
      signal,
      // Cancelled by the user: back to the dropzone, no error.
      onCancel: () => setProgress(null),
      onStage: (stage, message) => {
        setProgress({ stage, message, percent: PROGRESS_STAGE_PERCENT[stage] ?? 50 });
      },
      onComplete: async (data) => {
        const draft = {
          ...emptyExtracted,
          ...(data.extracted || {}),
          skills: data.extracted?.skills || [],
        };
        setProgress({ stage: "complete", message: "Your GuMi profile is ready. Saving your profile...", percent: 100 });
        try {
          const r = await saveCareerProfile(buildProfilePayload(resumeId, data.parse_run_id, draft));
          setUser((current) => (current ? { ...current, active_profile_id: r.data.profile_id, onboarded: true } : current));
          setProgress(null);
          onDone?.();
        } catch (err) {
          setProgress(null);
          setError(getApiErrorMessage(err, "Your profile was extracted but couldn't be saved automatically. Please try uploading again."));
        }
      },
      onError: (message) => {
        setProgress(null);
        setError(message || "We couldn't read your resume. Try re-uploading it, or email support@gumiscore.com if it keeps failing.");
      },
    });
  };

  const handleFile = async (file) => {
    if (busy || !file) return;
    setError("");
    if (file.size > MAX_BYTES) {
      setError("That file is larger than 5 MB. Please upload a smaller PDF or Word (.docx) file.");
      return;
    }
    setProgress({ stage: "reading", message: "Uploading your resume...", percent: 2 });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const r = await uploadResume(file, { signal: controller.signal });
      if (controller.signal.aborted) {
        setProgress(null);
        return;
      }
      runParse(r.data.resume_id, controller.signal);
    } catch (err) {
      setProgress(null);
      if (controller.signal.aborted) return; // cancelled by the user
      setError(getApiErrorMessage(err, "We couldn't upload your resume. Please try again, and email support@gumiscore.com if it keeps happening."));
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0] || null);
  };

  // While extraction/save is in flight, StageLoader is the only overlay
  // shown — this modal's own chrome (dropzone, columns) steps aside rather
  // than stacking two full-screen overlays.
  if (busy) {
    return (
      <StageLoader
        visible
        variant="resume"
        stage={progress?.stage}
        percent={progress?.percent}
        onCancel={() => abortRef.current?.abort()}
      />
    );
  }

  return createPortal(
    <>
      <div
        role="presentation"
        data-testid="resume-upload-modal-overlay"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Upload your resume"
          data-testid="resume-upload-modal"
          style={{
            width: "100%",
            maxWidth: 720,
            background: "white",
            borderRadius: 20,
            boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
            display: "flex",
            flexWrap: "wrap",
            overflow: "hidden",
          }}
        >
          {/* LEFT column */}
          <div
            style={{
              flex: "1 1 320px",
              background: "#f5f4f0",
              padding: 36,
              borderRadius: "20px 0 0 20px",
            }}
            className="resume-upload-modal-left"
          >
            <p style={{ fontSize: 22, fontWeight: 700, marginBottom: 24, color: "#1a1a18" }}>
              Gu<span style={{ color: "#1D9E75" }}>Mi</span>
            </p>
            <h2 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25, marginBottom: 12, color: "#1a1a18" }}>
              One upload. Every job scored.
            </h2>
            <p style={{ fontSize: 15, color: "#5f5e5a", lineHeight: 1.6, marginBottom: 20 }}>
              GuMi reads your full resume (skills, seniority, scale, and domain depth), then
              scores every job you paste against it.
            </p>
            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#5f5e5a", lineHeight: 2 }}>
              <div>✓ Builds your intelligence layer, once</div>
              <div>✓ PDF or Word (.docx) supported</div>
              <div>✓ Your resume stays private</div>
            </div>
          </div>

          {/* RIGHT column */}
          <div style={{ flex: "1 1 320px", padding: 36 }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <button
                type="button"
                onClick={onSkip}
                data-testid="resume-upload-modal-later"
                style={{
                  background: "none",
                  border: "none",
                  color: "#8a8880",
                  fontSize: 12.5,
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                I'll do this later
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={onInputChange}
              data-testid="resume-upload-modal-input"
              style={{ display: "none" }}
            />
            <div
              onClick={() => !busy && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="resume-upload-modal-dropzone"
              style={{
                border: `2px dashed #1D9E75`,
                background: dragOver ? "rgba(29,158,117,0.12)" : "rgba(29,158,117,0.05)",
                borderRadius: 16,
                padding: "40px 24px",
                textAlign: "center",
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              <p style={{ fontSize: 17, fontWeight: 700, marginBottom: 6, color: "#1a1a18" }}>
                Upload your resume
              </p>
              <p style={{ fontSize: 13, color: "#1a1a18", marginBottom: 4 }}>
                Drop it here or click to browse
              </p>
              <ResumeUploadHint style={{ fontSize: 13, color: "#5f5e5a" }} testId="resume-upload-modal-hint" />
            </div>

            {error && (
              <p style={{ marginTop: 16, fontSize: 13, color: "#C53030" }} data-testid="resume-upload-modal-error">
                {error}
              </p>
            )}

            <p style={{ marginTop: 20, fontSize: 12, color: "#8a8880", lineHeight: 1.6 }}>
              🔒 Your resume is private. GuMi only uses it to score jobs for you. It's never
              shared with recruiters or employers.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) {
          .resume-upload-modal-left { border-radius: 20px 20px 0 0 !important; }
        }
      `}</style>
    </>,
    document.body
  );
}
